"use client"

import { useEffect, useRef } from "react"
import { usePathname } from "next/navigation"

const INGEST_URL =
  process.env.NEXT_PUBLIC_ANALYTICS_INGEST_URL ??
  "https://dash-api.askfinz.ai/api/dash/analytics/ingest"

const FLUSH_INTERVAL_MS = 10_000

// SECURITY V-079 — hard-kill list. rrweb session-replay MUST NEVER run on
// these subdomains regardless of the user's consent state, because the DOM
// trees there contain HIPAA-class (med), GLBA-class (finance), and probate-
// class (estate) content. Even with `maskAllInputs:true` and `af-mask`,
// rendered text still ends up in the recording — the only safe option is
// "don't record at all".
const HARD_KILL_HOSTS: ReadonlyArray<string> = [
  "med.askfinz.ai",
  "finance.askfinz.ai",
  "estate.askfinz.ai",
]

// SECURITY V-079 — explicit consent flag. Stored in localStorage as the
// string "1" by the consent banner (or by an admin tool). Anything else
// (including "true", "yes", missing) is treated as no-consent.
const CONSENT_KEY = "AskFinz_recording_consent"

// CSS class authors should add to any element containing PII / PHI.
// rrweb's `maskTextClass` will replace the text node contents with `*`s in
// the recording, while still emitting the surrounding DOM structure.
//
// Usage:   <span className="af-mask">{patient.name}</span>
//
// Components in `med`, `finance`, `estate` are blocked entirely (see
// HARD_KILL_HOSTS), but the same `af-mask` class is honoured everywhere
// for cases where consent IS given (e.g. a chat user pasting their phone
// number into a message — they consented to recording, but we still mask
// the message body).
export const RECORDING_MASK_CLASS = "af-mask"

function isRecordingAllowed(): { allowed: boolean; reason: string } {
  if (typeof window === "undefined") return { allowed: false, reason: "ssr" }
  const host = window.location.hostname.toLowerCase()
  if (HARD_KILL_HOSTS.some((kill) => host === kill || host.endsWith("." + kill))) {
    return { allowed: false, reason: "hard-kill-host" }
  }
  let consent: string | null = null
  try {
    consent = localStorage.getItem(CONSENT_KEY)
  } catch {
    return { allowed: false, reason: "storage-unavailable" }
  }
  if (consent !== "1") return { allowed: false, reason: "no-consent" }
  return { allowed: true, reason: "ok" }
}

export function AnalyticsRecorder() {
  const pathname = usePathname()
  const pendingRef = useRef<unknown[]>([])
  const seqRef = useRef(0)
  const startRef = useRef(Date.now())
  const pagesRef = useRef<string[]>([])
  const stopRef = useRef<(() => void) | null>(null)
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null)

  useEffect(() => {
    if (typeof window === "undefined") return

    // SECURITY V-079: gate the entire recorder behind the consent + kill-list
    // check. If we don't have explicit consent, we don't even import rrweb
    // — neither bytes nor metadata leave the device.
    const gate = isRecordingAllowed()
    if (!gate.allowed) {
      // Surfacing this as a debug log helps operators verify the kill list
      // is doing its job; no PII is logged.
      try {
        // eslint-disable-next-line no-console
        console.debug("[V-079] recorder disabled:", gate.reason)
      } catch {
        /* empty */
      }
      return
    }

    // Get or create a stable session ID (resets when browser tab is closed)
    let sid = sessionStorage.getItem("_af_sid")
    if (!sid) {
      sid = crypto.randomUUID()
      sessionStorage.setItem("_af_sid", sid)
      sessionStorage.setItem("_af_start_url", window.location.href)
    }
    startRef.current = Date.now()
    pagesRef.current = [window.location.pathname]

    const flush = (useBeacon = false) => {
      const events = pendingRef.current.splice(0)
      if (!events.length) return

      const raw = localStorage.getItem("AskFinz_auth_user")
      let userId: string | null = null
      try { userId = raw ? (JSON.parse(raw)?.id ?? null) : null } catch { /* empty */ }

      const body = JSON.stringify({
        session_id: sid,
        app_domain: window.location.hostname,
        user_id: userId,
        user_agent: navigator.userAgent,
        start_url: sessionStorage.getItem("_af_start_url") ?? window.location.href,
        pages: [...pagesRef.current],
        duration_ms: Date.now() - startRef.current,
        events,
        seq: seqRef.current++,
      })

      if (useBeacon) {
        // Page hide/unload: sendBeacon survives navigation but has a ~64KB limit.
        // If it returns false (payload too large), fall back to keepalive fetch.
        const blob = new Blob([body], { type: "application/json" })
        const sent = navigator.sendBeacon(INGEST_URL, blob)
        if (!sent) {
          fetch(INGEST_URL, { method: "POST", headers: { "Content-Type": "application/json" }, body, keepalive: true }).catch(() => { /* silent */ })
        }
      } else {
        // Periodic flush: use regular fetch — no size limit, no keepalive needed.
        fetch(INGEST_URL, { method: "POST", headers: { "Content-Type": "application/json" }, body }).catch(() => { /* silent */ })
      }
    }

    // Periodic flush
    timerRef.current = setInterval(flush, FLUSH_INTERVAL_MS)

    // Flush on tab hide
    const onHide = () => { if (document.visibilityState === "hidden") flush(true) }
    document.addEventListener("visibilitychange", onHide)

    // Start rrweb recording (dynamic import avoids SSR breakage)
    import("rrweb").then(({ record }) => {
      let fullSnapshotSent = false
      const stop = record({
        emit(event) {
          pendingRef.current.push(event)
          // Send the initial FullSnapshot (type 2) immediately — it's too large
          // for sendBeacon and we can't afford to lose it in a delayed batch.
          if (!fullSnapshotSent && (event as { type: number }).type === 2) {
            fullSnapshotSent = true
            // Small delay lets rrweb finish emitting the Meta+FullSnapshot pair
            setTimeout(() => flush(), 0)
          }
        },
        // Don't record input values to preserve user privacy
        maskAllInputs: true,
        // SECURITY V-079: any element with `af-mask` has its text content
        // replaced with `*`s in the recording. Document this in the consent
        // banner so engineers know what to wrap PII with.
        maskTextClass: RECORDING_MASK_CLASS,
        blockClass: "af-no-record",
      })
      stopRef.current = stop ?? null
    }).catch(() => { /* rrweb unavailable — recording disabled */ })

    return () => {
      if (timerRef.current) clearInterval(timerRef.current)
      if (stopRef.current) stopRef.current()
      document.removeEventListener("visibilitychange", onHide)
      flush(true)
    }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  // Track page navigation
  useEffect(() => {
    if (pathname && !pagesRef.current.includes(pathname)) {
      pagesRef.current = [...pagesRef.current, pathname]
    }
  }, [pathname])

  return null
}

// SECURITY V-079 — minimal consent banner. Apps that want a richer banner
// (cookie-policy modal, etc.) can replace this with their own component;
// the only contract is that it sets `localStorage.AskFinz_recording_consent`
// to `"1"` after the user clicks accept, and `"0"` (or removes the key) on
// decline. The recorder will pick up the new state on the next mount.
//
// Hard-kill hosts (`med` / `finance` / `estate`) never show this banner —
// recording is impossible there regardless of the user's choice.
export function RecordingConsentBanner() {
  const pathname = usePathname()
  const visibleRef = useRef<boolean>(false)

  useEffect(() => {
    if (typeof window === "undefined") return
    const host = window.location.hostname.toLowerCase()
    if (HARD_KILL_HOSTS.some((k) => host === k || host.endsWith("." + k))) return
    try {
      const c = localStorage.getItem(CONSENT_KEY)
      if (c === "1" || c === "0") return
      // Show via a CustomEvent on the window — apps render the actual UI.
      visibleRef.current = true
      window.dispatchEvent(new CustomEvent("askfinz:recording-consent-required"))
    } catch {
      /* empty */
    }
  }, [pathname])

  return null
}
